/**
 * **The roster's own view, written into a URL and read back out of one.**
 *
 * The roster answers "which clients, in what order" client-side — `rosterView` filters by tab and
 * search and sorts by one of four orderings, none of which the backend can do. That made the answer
 * a piece of component state, and component state cannot leave the tab it lives in: a link pasted
 * into a second window, or sent to somebody, arrived with no list behind it at all.
 *
 * The client detail's `‹ Poprzednia / Następna ›` pair needs that list. "The next client" has no
 * meaning except as *the next one on the list the trainer is looking at* — walking to the next id
 * in the database would be a different list wearing the same two arrows. So the three values that
 * define the list travel in the address, and this module is the one place that decides how.
 *
 * ── Three parameters, and a DEFAULT is never written ──────────────────────────────────────────
 * `q` the search text, `f` the filter tab, `s` the ordering. A value equal to the roster's own
 * default is omitted rather than serialised, for two reasons: the common address stays
 * `/klienci/12` rather than `/klienci/12?q=&f=all&s=attention`, and — the load-bearing one — a
 * BARE link then reads back as exactly the list a trainer sees when they open the roster fresh.
 * That is what makes an address with no list context still answerable rather than a dead end.
 *
 * The cost is stated rather than hidden: a bare link's neighbours are the DEFAULT roster's, which
 * may not be the list the person who sent it was looking at. Nothing in a bare URL can say
 * otherwise, and the alternative — two arrows that refuse on every link a trainer bookmarks — was
 * judged the worse of the two.
 *
 * ── Reading is total, and never throws ────────────────────────────────────────────────────────
 * Every unrecognised value falls back to its default. A stale link carrying `?s=alphabetical` from
 * some future build opens the roster's default ordering instead of selecting nothing and saying
 * nothing about it, which is the same rule `isRosterOrder` exists for.
 */

import {
  DEFAULT_ROSTER_ORDER,
  DEFAULT_ROSTER_TAB,
  isRosterOrder,
  isRosterTab,
  type RosterOrder,
  type RosterTab,
} from './rosterView';

/** The three keys as they appear in an address. Every one of them is optional. */
export interface RosterContextSearch {
  readonly q?: string;
  readonly f?: RosterTab;
  readonly s?: RosterOrder;
}

/** The same three, resolved — what `rosterView` needs, with nothing left to decide. */
export interface RosterContext {
  readonly search: string;
  readonly tab: RosterTab;
  readonly order: RosterOrder;
}

export const DEFAULT_ROSTER_CONTEXT: RosterContext = {
  search: '',
  tab: DEFAULT_ROSTER_TAB,
  order: DEFAULT_ROSTER_ORDER,
};

/**
 * Read a URL's search object as a list context.
 *
 * Takes `unknown` per key rather than a typed shape: this is the boundary, and the router hands
 * over whatever the address bar held. A trimmed empty `q` is the default, so `?q=%20` and no `q`
 * at all describe the same list.
 */
export function rosterContextOf(raw: {
  readonly q?: unknown;
  readonly f?: unknown;
  readonly s?: unknown;
}): RosterContext {
  const search = typeof raw.q === 'string' ? raw.q.trim() : '';
  const tab = typeof raw.f === 'string' && isRosterTab(raw.f) ? raw.f : DEFAULT_ROSTER_TAB;
  const order = typeof raw.s === 'string' && isRosterOrder(raw.s) ? raw.s : DEFAULT_ROSTER_ORDER;
  return { search, tab, order };
}

/**
 * Write a list context into a URL's search object, omitting every value that is already the
 * default. The inverse of `rosterContextOf` on everything that round-trips.
 */
export function rosterContextSearch(context: RosterContext): RosterContextSearch {
  const search = context.search.trim();
  return {
    ...(search === '' ? {} : { q: search }),
    ...(context.tab === DEFAULT_ROSTER_TAB ? {} : { f: context.tab }),
    ...(context.order === DEFAULT_ROSTER_ORDER ? {} : { s: context.order }),
  };
}
